"use client";

import type { Process, ProcessStep, ScreenCapture } from "@zetalabs/core";
import { ProcessStatus } from "@zetalabs/core";
import { useState } from "react";
import { Icon, type IconKind } from "../icon";
import { HStack, VStack } from "../layout/stack";
import { cn } from "../lib/utils";
import {
	IdleIndicator,
	ProgressIndicator,
	type ProgressIndicatorOverrides,
} from "../loaders/progress-indicator";
import { Skeleton } from "../skeleton";
import { LiveView } from "./live-view";
import { SubProcessRenderer } from "./sub-process-renderer";

export function ProcessTitle({
	children,
}: {
	children?: React.ReactNode;
}): React.ReactElement {
	const isString = typeof children === "string";

	if (isString && children.length === 0) {
		return (
			<div className="w-1/2 h-3 overflow-hidden rounded-full bg-foreground-lightest">
				<Skeleton />
			</div>
		);
	}

	return (
		<h6
			className="line-clamp-2 lg:line-clamp-2"
			title={isString ? children : ""}
		>
			{children}
		</h6>
	);
}
export interface ProcessRendererProps {
	data: Process;
	icon?: IconKind;
	isOpen?: boolean;
	statusMessage?: string;
	progress?: number;
	isTaskTakingLonger?: boolean;
}

export interface ProgressData {
	active: ProcessStep | null;
	completed: number;
	total: number;
}

export function ProcessRenderer({
	data,
	icon,
	isOpen = false,
	statusMessage,
	isTaskTakingLonger,
	progress = -1,
	...props
}: ProcessRendererProps): React.ReactElement {
	const [collapsed, setCollapsed] = useState<boolean>(Boolean(!isOpen));
	const { title, root } = data;
	const { duration, children: subProcesses, status } = root;

	const isReady = status >= ProcessStatus.RUNNING;
	const isCollapsable = subProcesses.length > 0;
	const isCanceled = data.root.status === ProcessStatus.CANCELLED;

	const overrides: Partial<ProgressIndicatorOverrides> = {};

	let iconElement = null;

	if (isTaskTakingLonger && !isCanceled) {
		iconElement = <IdleIndicator />;
	} else if (icon) {
		iconElement = <Icon className="text-message-surface-agent" kind={icon} />;
	}

	if (!collapsed) {
		overrides.status = "success";
	}

	if (isCollapsable) {
		return (
			<div {...props}>
				<HStack
					asChild
					className="items-center pl-4 pr-4 lg:pr-8 py-4 gap-2 lg:gap-4"
				>
					<button
						className="w-full"
						onClick={() => {
							setCollapsed((state) => !state);
						}}
						type="button"
					>
						<ProgressIndicator
							done={isReady}
							duration={duration}
							icon={iconElement}
							isCanceled={isCanceled}
							isTaskTakingLonger={isTaskTakingLonger}
							overrides={overrides}
							progress={progress}
						/>
						<VStack className="grow gap-0 text-left">
							<ProcessTitle>{title}</ProcessTitle>
							{statusMessage ? (
								<span className="text-message-text-agent-secondary text-sm truncate w-[90%] text-wrap">
									{statusMessage}
								</span>
							) : null}
						</VStack>
						{subProcesses.length > 0 ? (
							<Icon
								className="text-message-text-light"
								kind={collapsed ? "expand" : "collapse"}
							/>
						) : null}
					</button>
				</HStack>
				<VStack
					className={cn(
						"py-4 pb-6 border-t-message-border-agent border-t-[1px]",
						collapsed ? "hidden" : "",
					)}
				>
					{"liveView" in data && data.liveView ? (
						<LiveView data={data.liveView as ScreenCapture[]} />
					) : null}
					{subProcesses.map((s) => (
						<SubProcessRenderer
							data={s}
							isActive
							isCanceled={isCanceled}
							key={s.id}
						/>
					))}
				</VStack>
			</div>
		);
	}

	return (
		<div {...props}>
			<HStack className="items-center p-4 lg:pr-12 gap-2 lg:gap-4">
				<ProgressIndicator
					done={status === ProcessStatus.DONE}
					duration={duration}
					icon={
						icon ? (
							<Icon className="text-message-surface-agent" kind={icon} />
						) : null
					}
					progress={progress}
				/>
				<ProcessTitle>{title}</ProcessTitle>
			</HStack>
		</div>
	);
}
